/**
 * Util to set a jail for focus,
 * tries to prevent the browser from setting focus to nodes
 * which are not contained in the given parent node
 *
 * @author tku, bgr
 */
import { getTabbables } from "./tabbable";

function tryFocus(node) {
	try {
		node.focus();
	} catch {
		// Ignored.
	}
}

let oldFocusedNode = null;
let handler = null;
let escapeResolver = null;

export function unlock() {
	if (handler) {
		document.removeEventListener("keydown", handler);
		handler = null;
		escapeResolver = null;
		if (oldFocusedNode) {
			tryFocus(oldFocusedNode);
		}
	}
}

export function lockUntilEscaped(node, focusFirstTabbable = true) {
	if (handler) {
		unlock();
	}

	const tabbables = getTabbables(node);
	const firstTabbableNode = tabbables.length > 0 ? tabbables[0] : node;
	const lastTabbableNode =
		tabbables.length > 0 ? tabbables[tabbables.length - 1] : node;

	if (focusFirstTabbable) {
		oldFocusedNode = document.activeElement;
		tryFocus(firstTabbableNode);
	}

	handler = event => {
		if (event.key == "Escape") {
			escapeResolver();
			unlock();
		}

		if (event.key != "Tab") {
			return;
		}

		// There are no focus nodes to cycle through so just skip the tab key event
		if (firstTabbableNode == lastTabbableNode) {
			event.stopPropagation();
			event.preventDefault();
			return;
		}

		const currentFocusedNode = event.target;

		// Move focus from last item in dialog to first item
		if (currentFocusedNode == lastTabbableNode && !event.shiftKey) {
			tryFocus(firstTabbableNode);
			event.stopPropagation();
			event.preventDefault();
			return;
		}

		// Move focus from first item in dialog to last one if we're
		// cycling through items using the SHIFT key
		if (currentFocusedNode == firstTabbableNode && event.shiftKey) {
			tryFocus(lastTabbableNode);
			event.stopPropagation();
			event.preventDefault();
			return;
		}
	};

	document.addEventListener("keydown", handler);

	return new Promise(resolve => {
		escapeResolver = resolve;
	});
}
